import { clampRect, defaultRect, EDGE_GAP, PAGE_RATIO, TILE_GAP, zoneRect, type Rect, type Size, type Zone } from './geometry';

/**
 * Window layouts — a small vocabulary borrowed from desktop window managers
 * (halves, quarters, thirds, centre, almost-maximise) and a few arrangements
 * for every window at once. They act only on MyTXT's own windows, on its own
 * canvas. Pure functions: the canvas size is always an argument.
 */

/** A layout for one window. Zones dock (and re-dock when the canvas changes); the others float. */
export type WindowLayout = Zone | 'center' | 'default';

/** Arrangements for all windows. */
export type Arrangement = 'auto' | 'columns' | 'grid' | 'cascade';

/** The smallest a window may become when the canvas is shared out. */
export const TILE_MIN: Size = { w: 320, h: 240 };


/** Where one window goes for a layout. `current` is its present rectangle (for centring at its size). */
export function layoutRect(layout: WindowLayout, canvas: Size, current?: Rect): Rect {
  if (layout === 'default') return defaultRect(canvas);
  if (layout === 'center') {
    const base = current ?? defaultRect(canvas);
    return clampRect({ ...base, x: Math.round((canvas.w - base.w) / 2), y: Math.round((canvas.h - base.h) / 2) }, canvas);
  }
  return zoneRect(layout, canvas);
}

/** A layout is offered only where it leaves a usable window. */
export function layoutFits(layout: WindowLayout, canvas: Size): boolean {
  const r = layoutRect(layout, canvas);
  return r.w >= TILE_MIN.w && r.h >= TILE_MIN.h;
}

const round = (r: Rect): Rect => ({ x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.w), h: Math.round(r.h) });

/** Columns × rows that hold `count` cells of at least TILE_MIN, closest to page-shaped. */
function bestGrid(count: number, canvas: Size, inset: number): { cols: number; rows: number } | null {
  const innerW = canvas.w - 2 * inset;
  const innerH = canvas.h - 2 * inset;
  let best: { cols: number; rows: number; score: number } | null = null;
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    // A grid with a whole empty row is never the best answer.
    if (cols * (rows - 1) >= count) continue;
    const cw = (innerW - (cols - 1) * TILE_GAP) / cols;
    const ch = (innerH - (rows - 1) * TILE_GAP) / rows;
    if (cw < TILE_MIN.w || ch < TILE_MIN.h) continue;
    const score = Math.abs(Math.log(cw / ch / PAGE_RATIO)) + (cols * rows - count) * 0.12;
    if (!best || score < best.score) best = { cols, rows, score };
  }
  return best;
}

/** An even grid; a short last row is centred. Cells never grow wider than a comfortable page. */
function grid(count: number, canvas: Size, cols: number, rows: number, inset: number, capWidth: boolean): Rect[] {
  const innerW = canvas.w - 2 * inset;
  const innerH = canvas.h - 2 * inset;
  const cw = (innerW - (cols - 1) * TILE_GAP) / cols;
  const ch = (innerH - (rows - 1) * TILE_GAP) / rows;
  const w = capWidth ? Math.min(cw, Math.max(ch * PAGE_RATIO, 560)) : cw;
  const rects: Rect[] = [];
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const inRow = row === rows - 1 ? count - row * cols : cols;
    const rowW = inRow * w + (inRow - 1) * TILE_GAP;
    rects.push(round({ x: inset + (innerW - rowW) / 2 + col * (w + TILE_GAP), y: inset + row * (ch + TILE_GAP), w, h: ch }));
  }
  return rects;
}

/**
 * Cascade: page-sized windows offset down and to the right so every head
 * stays visible. Used on its own, and as the fallback when the canvas cannot
 * share itself out without making windows unusably small.
 */
export function cascade(count: number, canvas: Size): Rect[] {
  const step = 30;
  const base = defaultRect(canvas);
  const w = Math.max(Math.min(base.w, canvas.w - 2 * EDGE_GAP - step * Math.min(count - 1, 8)), Math.min(TILE_MIN.w, canvas.w - 2 * EDGE_GAP));
  const h = Math.max(Math.min(base.h * 0.9, canvas.h - 2 * EDGE_GAP - step * Math.min(count - 1, 8)), Math.min(TILE_MIN.h, canvas.h - 2 * EDGE_GAP));
  // Fit as many steps as the canvas allows, then start a new stack beside (or over) the first.
  const fitX = Math.max(1, Math.floor((canvas.w - 2 * EDGE_GAP - w) / step) + 1);
  const fitY = Math.max(1, Math.floor((canvas.h - 2 * EDGE_GAP - h) / step) + 1);
  const perStack = Math.max(1, Math.min(fitX, fitY, count));
  const stacks = Math.ceil(count / perStack);
  const spanW = w + (perStack - 1) * step;
  const totalW = Math.min(canvas.w - 2 * EDGE_GAP, spanW + (stacks - 1) * Math.max(step * 3, Math.min(w * 0.6, (canvas.w - 2 * EDGE_GAP - spanW) / Math.max(1, stacks - 1))));
  const left = Math.max(EDGE_GAP, (canvas.w - totalW) / 2);
  const top = Math.max(EDGE_GAP, (canvas.h - (h + (perStack - 1) * step)) / 2);
  const stackGap = stacks > 1 ? (totalW - spanW) / (stacks - 1) : 0;
  const rects: Rect[] = [];
  for (let i = 0; i < count; i++) {
    const stack = Math.floor(i / perStack);
    const k = i % perStack;
    rects.push(clampRect(round({ x: left + stack * stackGap + k * step, y: top + k * step, w, h }), canvas));
  }
  return rects;
}

/** A pair, optically balanced: two pages side by side around the centre, or one above the other on a tall canvas. */
function pair(canvas: Size): Rect[] | null {
  const m = { x: Math.min(96, Math.max(EDGE_GAP + 8, canvas.w * 0.05)), y: Math.min(64, Math.max(EDGE_GAP + 8, canvas.h * 0.06)) };
  const h = canvas.h - 2 * m.y;
  const w = Math.min((canvas.w - 2 * m.x - TILE_GAP * 2) / 2, Math.max(560, Math.min(720, h * PAGE_RATIO)));
  if (w >= TILE_MIN.w && h >= TILE_MIN.h) {
    const gap = TILE_GAP * 2;
    const left = (canvas.w - (2 * w + gap)) / 2;
    return [round({ x: left, y: m.y, w, h }), round({ x: left + w + gap, y: m.y, w, h })];
  }
  const g = bestGrid(2, canvas, EDGE_GAP + 8);
  return g ? grid(2, canvas, g.cols, g.rows, EDGE_GAP + 8, false) : null;
}

/**
 * Arrange `count` windows. One window returns to its first, centred size;
 * two become a balanced pair; more share the canvas in the most page-like
 * grid that keeps every window usable — and when none does, they cascade.
 */
export function arrange(count: number, canvas: Size, mode: Arrangement = 'auto'): Rect[] {
  if (count <= 0) return [];
  if (mode === 'cascade') return cascade(count, canvas);
  if (count === 1) return [defaultRect(canvas)];
  const inset = EDGE_GAP + 8;
  if (mode === 'columns') {
    const cw = (canvas.w - 2 * inset - (count - 1) * TILE_GAP) / count;
    if (cw >= TILE_MIN.w) return grid(count, canvas, count, 1, inset, false);
    mode = 'grid';
  }
  if (mode === 'auto' && count === 2) return pair(canvas) ?? cascade(count, canvas);
  const g = bestGrid(count, canvas, inset);
  if (!g) return cascade(count, canvas);
  return grid(count, canvas, g.cols, g.rows, inset, mode === 'auto');
}

/** Give other windows the size of `model`, each keeping its own centre, all of them reachable. */
export function matchSize(model: Rect, others: readonly Rect[], canvas: Size): Rect[] {
  return others.map((r) => clampRect({ x: r.x + r.w / 2 - model.w / 2, y: r.y + r.h / 2 - model.h / 2, w: model.w, h: model.h }, canvas));
}

/** Reading order of rectangles (rows, then left to right): arrangements keep windows close to where they were. */
export function readingOrder<T extends { rect: Rect }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => {
    const ra = Math.round((a.rect.y + a.rect.h / 2) / 160);
    const rb = Math.round((b.rect.y + b.rect.h / 2) / 160);
    return ra - rb || a.rect.x - b.rect.x;
  });
}

/** True when no two rectangles overlap and all lie inside the canvas. */
export function isTiling(rects: readonly Rect[], canvas: Size): boolean {
  const inside = rects.every((r) => r.x >= 0 && r.y >= 0 && r.x + r.w <= canvas.w && r.y + r.h <= canvas.h);
  const apart = rects.every((a, i) => rects.every((b, j) => i >= j || a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y));
  return inside && apart;
}
